import { forwardRef, ReactNode, useEffect, useRef } from 'react';

/**
 * Utility hook that provides callbacks to handle dragstart and dragend events at the window-level
 */
export const useGlobalDragEvents = ({
  onDragStart,
  onDragEnd,
}: {
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) => {
  useEffect(() => {
    const handleDragEnd = () => {
      if (onDragEnd) onDragEnd();
    };
    const handleDragStart = () => {
      if (onDragStart) onDragStart();
    };

    window.addEventListener('dragstart', handleDragStart);
    window.addEventListener('dragend', handleDragEnd);

    return () => {
      window.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('dragend', handleDragEnd);
    };
  }, [onDragStart, onDragEnd]);
};

/**
 * CONTEXT:
 * The drag enter/leave event fires everytime an element is dragged into/outof the listening element AND its children.
 * This means that the events are highly noisy and in practice not very practical to use. This implemention uses a
 * counter to help us isolate and only fire an event when the parent element is entered and exited, ignoring children.
 */

interface DragBoundProps {
  onDragEnter?: () => void;
  onDragLeave?: () => void;
  children: ReactNode;
}

/**
 * This component exposes an augmented version of the onDragEnter and onDragLeave event handlers that will not re-fire for child elements.
 * Otherwise, this component is identical to a div element.
 */
export const DragBound = forwardRef<HTMLDivElement, DragBoundProps>(
  ({ children, onDragEnter, onDragLeave, ...restProps }, ref) => {
    const counterRef = useRef(0);
    // reset counter when drag ends anywhere in the window
    useGlobalDragEvents({
      onDragEnd: () => {
        counterRef.current = 0;
      },
    });

    const handleDragEnter = () => {
      counterRef.current++;
      if (counterRef.current === 1) {
        if (onDragEnter) onDragEnter();
      }
    };

    const handleDragLeave = () => {
      counterRef.current--;
      if (counterRef.current === 0) {
        if (onDragLeave) onDragLeave();
      }
    };

    return (
      <div
        ref={ref}
        data-test='drag-bound'
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        {...restProps}
      >
        {children}
      </div>
    );
  },
);
